import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import md5 from 'md5';
import stableStringify from 'json-stable-stringify';

import {apiClient} from 'common/util/api-client';
import type {Bookmark} from 'common/report/dashboards/types';
import {ReportName} from 'common/report/constants';
import {generateBookmarkURLSlug} from 'common/report/util/bookmark-urls';

import {appApiUrl} from '../utils';

export type BookmarkURLResponse = {
  slug: string;
  created_at: string;
  bookmark?: Bookmark;
  user_id: number;
  project_id: number;
  workspace_id: number;
  type: ReportName;
  bookmark_id?: number;
  params: Obj;
  name?: string;
  description?: string;
  overrides?: Obj;
};

type BookmarkURLParams = {
  project_id: number;
  workspace_id: number;
  type: ReportName;
  bookmark_id?: number;
  params: Obj;
  name?: string;
  description?: string;
  overrides?: Obj;
};

function buildEndpoint({projectId, slug}: {projectId: number; slug?: string}): string {
  return appApiUrl(`bookmark-urls/${slug ? `${slug}/` : ``}`, {projectId});
}

const previousBookmarkSlugs = new Map<string, string>();

/**
 * Gets URL slug based on bookmark params. Reuses previous slugs if params don't change.
 */
function getSlug(
  params: BookmarkURLParams,
): {
  slug: string;
  reused: boolean;
} {
  const paramsHash = md5(stableStringify(params));
  const previousSlug = previousBookmarkSlugs.get(paramsHash);

  if (previousSlug) {
    return {slug: previousSlug, reused: true};
  } else {
    const slug = generateBookmarkURLSlug();
    previousBookmarkSlugs.set(paramsHash, slug);

    return {slug, reused: false};
  }
}

/**
 * Retrieves a bookmark by slug.
 */
export async function fetchBookmarkUrl(params: {projectId: number; slug: string}) {
  return apiClient.get<BookmarkURLResponse>(buildEndpoint(params), null, {
    updateFetchCache: true,
  });
}

/**
 * Resets bookmark URL cache so previous slugs won't be reused.
 */
export function resetBookmarkUrlCache() {
  previousBookmarkSlugs.clear();
}

/**
 * Gets (and saves if necessary) a bookmark slug given params.
 */
export function getBookmarkUrl(params: BookmarkURLParams): string {
  const {slug, reused} = getSlug(params);

  if (!reused) {
    const requestParams = omitBy(omit(params, [`project_id`]), (value) => isNil(value));

    apiClient.post<BookmarkURLResponse>(buildEndpoint({projectId: params[`project_id`]}), {
      slug,
      ...requestParams,
    });
  }

  return slug;
}
